import type { CacheDateAndKey } from "@/types/common";
import type { NotificationCount, User } from "@/types/models";
import type { IWholesalePrice } from "@/types/product";
import { isObjectEmpty } from "@/utils/appUtil";
import {
  AppAuthRefeshTokenKey,
  AppAuthTokenCreatedKey,
  AppAuthTokenExpireKey,
  AppAuthTokenKey,
  CacheDateCheckKey,
  CacheKey,
  CategoryWholesaleCacheKey,
  FcmSettingKey,
  FcmTokenKey,
  LatestSyncActiveStatusKey,
  NotifyKey,
  PromotionCodeCacheKey,
  SearchHistoryKey,
  UserCacheKey
} from "@/libs/constant";
import { getDateNow, getMonthNow, getYearNow } from "@/utils/dateUtil";
import { useStorage } from "@vueuse/core";

export const useCache = () => {
  // Basic cache storage
  const SearchHistoryList = useStorage(SearchHistoryKey, [] as any[]);
  const cacheKeyCheckList = useStorage(
    CacheDateCheckKey,
    [] as CacheDateAndKey[]
  );
  const cacheYearAvailables = useStorage(
    CacheKey.YEAR_AVAILABLE,
    [] as number[]
  );

  // System cache storage
  const latestSyncActiveStatus = useStorage(LatestSyncActiveStatusKey, 0);
  const fcmSetting = useStorage(FcmSettingKey, true as boolean);
  const fcmToken = useStorage(FcmTokenKey, null as string | null | undefined);
  const refeshTokenKey = useStorage<string | null>(AppAuthRefeshTokenKey, null);
  const authTokenKey = useStorage<string | null>(AppAuthTokenKey, null);
  const authTokenExpireKey = useStorage<string | null>(
    AppAuthTokenExpireKey,
    null
  );
  const authTokenCreatedKey = useStorage<string | null>(
    AppAuthTokenCreatedKey,
    null
  );
  const cacheNotifyCount = useStorage(NotifyKey, {} as NotificationCount);

  // User cache storage
  const cachedUserInfo = useStorage<User | null | string>(UserCacheKey, null);

  // Promotion code cache storage
  const cachedPromotionCode = useStorage<string | null>(
    PromotionCodeCacheKey,
    null
  );

  const cachedCategoryWholesalePrices = useStorage<IWholesalePrice[]>(
    CategoryWholesaleCacheKey,
    [] as IWholesalePrice[]
  );

  // Generic cache key management
  const findByKey = (k: string): CacheDateAndKey | undefined => {
    if (cacheKeyCheckList.value && cacheKeyCheckList.value.length > 0) {
      return cacheKeyCheckList.value.find((e: CacheDateAndKey) => e.key == k);
    }
    return undefined;
  };

  const findIndexByKey = (k: string): number => {
    if (!cacheKeyCheckList.value) {
      return -1;
    }
    return cacheKeyCheckList.value.findIndex(
      (e: CacheDateAndKey) => e.key == k
    );
  };

  const onRemoveKey = (k: string): void => {
    const index = findIndexByKey(k);
    if (index >= 0) {
      cacheKeyCheckList.value.splice(index, 1);
    }
  };

  const onAddNewKey = (k: string): void => {
    const item = findByKey(k);
    if (!cacheKeyCheckList.value) {
      cacheKeyCheckList.value = [];
    }
    if (!item) {
      const d = getDateNow();
      cacheKeyCheckList.value.push({
        key: k,
        date: d.toLocaleDateString()
      });
    }
  };

  const onUpdateKey = (
    k: string,
    period: "DAY" | "MONTH" | "YEAR" = "DAY"
  ): void => {
    onRemoveKey(k);
    if (period == "DAY") {
      onAddNewKey(k);
    } else if (period == "MONTH") {
      onAddNewMonthKey(k);
    } else if (period == "YEAR") {
      onAddNewYearKey(k);
    }
  };

  const onAddNewMonthKey = (k: string): void => {
    const item = findByKey(k);
    if (!item) {
      cacheKeyCheckList.value.push({
        key: k,
        date: getMonthNow()
      });
    }
  };

  const onAddNewYearKey = (k: string): void => {
    const item = findByKey(k);
    if (!item) {
      cacheKeyCheckList.value.push({
        key: k,
        date: getYearNow()
      });
    }
  };

  // Cache validation functions
  const canFecthToServerToday = (k: string): boolean => {
    const item = findByKey(k);
    if (!item) {
      return true;
    }
    const d = getDateNow();
    return item.date != d.toLocaleDateString();
  };

  const canFecthToServerMonth = (k: string): boolean => {
    const item = findByKey(k);
    if (!item) {
      return true;
    }
    const m = getMonthNow();
    return item.date != m;
  };

  const canFecthToServerYear = (k: string): boolean => {
    const item = findByKey(k);
    if (!item) {
      return true;
    }
    const y = getYearNow();
    return item.date != y;
  };

  // Cache fetch validation
  const fetchFromCacheToday = (key: string, cache: any) => {
    const canFetchToServer = canFecthToServerToday(key);
    return !canFetchToServer && cache && !isObjectEmpty(cache);
  };

  const fetchFromCacheMonth = (key: string, cache: any) => {
    const canFetchToServer = canFecthToServerMonth(key);
    return !canFetchToServer && cache && !isObjectEmpty(cache);
  };

  const fetchFromCacheList = (canFetchToServer: boolean, cache: any) => {
    return !canFetchToServer && cache && cache.length > 0;
  };

  // ===== USER CACHE OPERATIONS =====

  const saveUserToCache = (user: User) => {
    cachedUserInfo.value = JSON.stringify(user);
  };

  const getUserFromCache = (): User | null => {
    if (process.env.CLIENT) {
      return cachedUserInfo.value
        ? (JSON.parse(cachedUserInfo.value as string) as User)
        : null;
    }
    return null;
  };

  const clearUserCache = () => {
    cachedUserInfo.value = null;
  };

  // ===== PROMOTION CODE CACHE OPERATIONS =====

  const getPromotionCodeFromCache = (): string | null => {
    return cachedPromotionCode.value;
  };

  const setPromotionCodeToCache = (code: string): void => {
    if (code && code.trim()) {
      cachedPromotionCode.value = code.trim();
    }
  };

  const clearPromotionCodeCache = () => {
    cachedPromotionCode.value = null;
  };

  const hasPromotionCodeInCache = (): boolean => {
    return !!cachedPromotionCode.value;
  };

  const getCategoryWholesalePricesFromCache = (): IWholesalePrice[] => {
    return cachedCategoryWholesalePrices.value || [];
  };

  const setCategoryWholesalePricesToCache = (
    prices: IWholesalePrice[]
  ): void => {
    if (prices && Array.isArray(prices)) {
      cachedCategoryWholesalePrices.value = prices;
    }
  };

  const getCategoryWholesalePricesByCategoryFromCache = (
    categoryId: string
  ): IWholesalePrice[] => {
    const allPrices = getCategoryWholesalePricesFromCache();
    return allPrices.filter(
      price => price.category_id === categoryId && price.is_active
    );
  };

  const clearCategoryWholesaleCache = (): void => {
    cachedCategoryWholesalePrices.value = [];
    onRemoveKey(CategoryWholesaleCacheKey);
  };
  // ===== CACHE CLEANUP OPERATIONS =====

  const destroyCache = () => {
    cacheKeyCheckList.value = null;
    cacheYearAvailables.value = null;
  };

  const logoutClear = () => {
    cacheNotifyCount.value = null;
    cachedUserInfo.value = null;
    cachedPromotionCode.value = null;
    destroyCache();
  };

  const clearAllUserCache = (): void => {
    cachedUserInfo.value = null;
    cachedPromotionCode.value = null;
  };

  return {
    // Basic cache operations
    findByKey,
    onAddNewKey,
    onUpdateKey,
    onRemoveKey,
    onAddNewMonthKey,
    onAddNewYearKey,
    canFecthToServerToday,
    canFecthToServerMonth,
    canFecthToServerYear,
    fetchFromCacheToday,
    fetchFromCacheMonth,
    fetchFromCacheList,
    destroyCache,
    logoutClear,

    // Cache storage references
    SearchHistoryList,
    cacheKeyCheckList,
    cacheYearAvailables,
    latestSyncActiveStatus,
    fcmSetting,
    fcmToken,
    refeshTokenKey,
    authTokenKey,
    authTokenExpireKey,
    authTokenCreatedKey,
    cacheNotifyCount,

    // User cache operations
    cachedUserInfo,
    saveUserToCache,
    getUserFromCache,
    clearUserCache,
    clearAllUserCache,

    // Promotion code cache operations
    cachedPromotionCode,
    getPromotionCodeFromCache,
    setPromotionCodeToCache,
    clearPromotionCodeCache,
    hasPromotionCodeInCache,

    cachedCategoryWholesalePrices,
    getCategoryWholesalePricesFromCache,
    setCategoryWholesalePricesToCache,
    getCategoryWholesalePricesByCategoryFromCache,
    clearCategoryWholesaleCache
  };
};
